/**
 * ClientReplyCapture · iter303ak-fix25 — Declined · T20 · Action 10
 * ────────────────────────────────────────────────────────────────
 * Logs the client's response to the declined letter:
 *   • Channel radio (Email / SMS / Phone / In-person)
 *   • Datetime picker (defaults to now)
 *   • Optional note field
 * Writes to `declined_reply` = { channel, replied_at, note }.
 */
import React, { useState, useEffect } from 'react';
import { MessageSquare, Mail, Phone, Users, CheckCircle2 } from 'lucide-react';

const CHANNELS = [
  { key: 'email',     label: 'Email',     Icon: Mail },
  { key: 'sms',       label: 'SMS',       Icon: MessageSquare },
  { key: 'phone',     label: 'Phone',     Icon: Phone },
  { key: 'in_person', label: 'In-person', Icon: Users },
];

function _toLocalDatetime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    // ISO to `YYYY-MM-DDTHH:mm` for datetime-local input.
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
}

export default function ClientReplyCapture({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const initial = value || {};
  const [channel, setChannel] = useState(initial.channel || '');
  const [repliedAtLocal, setRepliedAtLocal] = useState(_toLocalDatetime(initial.replied_at || new Date().toISOString()));
  const [note, setNote] = useState(initial.note || '');

  const valid = !!channel && !!repliedAtLocal;

  useEffect(() => {
    if (!onChange) return;
    let iso = '';
    try {
      if (repliedAtLocal) iso = new Date(repliedAtLocal).toISOString();
    } catch { /* silent */ }
    onChange({ channel, replied_at: iso, note });
  }, [channel, repliedAtLocal, note, onChange]);

  return (
    <div className="space-y-4" data-testid="wf-client-reply-capture">
      <div className="rounded-xl border border-purple-200 bg-purple-50/60 p-4">
        <div className="flex items-start gap-3">
          <MessageSquare className="w-5 h-5 mt-0.5 shrink-0 text-purple-700" />
          <div>
            <div className="text-sm font-semibold text-purple-900">Log the Client's Response</div>
            <div className="text-sm mt-1 text-purple-800">
              {params.prompt || 'Pick the channel the client used to reply, when it happened, and drop a short note if useful.'}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-2">Channel <span className="text-rose-500">*</span></label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {CHANNELS.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setChannel(key)}
                data-testid={`wf-reply-channel-${key}`}
                className={`p-3 border rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition ${
                  channel === key
                    ? 'border-purple-500 bg-purple-100 text-purple-900 shadow-sm'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1">Replied at <span className="text-rose-500">*</span></label>
          <input
            type="datetime-local"
            value={repliedAtLocal}
            onChange={(e) => setRepliedAtLocal(e.target.value)}
            className="w-full max-w-xs px-3 py-2 text-sm border border-slate-300 rounded"
            data-testid="wf-reply-datetime"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1">Note (optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="e.g. Client said budget dropped after tax return; may revisit next quarter."
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded resize-y"
            data-testid="wf-reply-note"
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-reply-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-reply-cancel">Cancel</button>}
          <button
            onClick={onNext}
            disabled={!valid}
            className="px-4 py-1.5 rounded-md text-sm bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-reply-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
